import { CTABanner } from '@/components/cta-banner';
import { Section } from '@/components/section';
import { SEO } from '@/components/seo';
import { company } from '@/data/company';
import { ContactForm } from '@/features/contact-form/contact-form';
import { FAQSection } from '@/features/faq/faq-section';
import { HomeHero } from '@/features/hero/home-hero';
import { ProjectsTeaser } from '@/features/projects-gallery/projects-teaser';
import { ReviewsSection } from '@/features/reviews/reviews-section';
import { ServiceAreaSection } from '@/features/service-area/service-area-section';
import { ServiceOverview } from '@/features/service-overview/service-overview';
import { TrustBar } from '@/features/trust-bar/trust-bar';

const localBusinessJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: company.name,
  url: company.url,
  telephone: company.phone.international,
  email: company.email.primary,
  address: {
    '@type': 'PostalAddress',
    streetAddress: company.address.street,
    postalCode: company.address.postalCode,
    addressLocality: company.address.city,
    addressCountry: 'DE',
  },
  areaServed: company.serviceAreaCities,
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00',
      closes: '18:00',
    },
  ],
};

export function HomePage(): React.ReactNode {
  return (
    <>
      <SEO
        title={`Baumpflege in ${company.serviceArea} – Straesser`}
        description={`Professionelle Baumpflege, Baumhausbau und Baumfällung in ${company.serviceArea}. Seilklettertechnik, kostenlose Begutachtung, 24/7 Notfälle.`}
        path="/"
        jsonLd={localBusinessJsonLd}
      />
      <HomeHero />
      <TrustBar />
      <ServiceOverview />
      <Section background="paper">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <h2 className="mb-4 text-3xl font-semibold sm:text-4xl">Wir arbeiten am Baum</h2>
            <p className="mb-4 text-mute">
              Wir kommen zu Ihnen, schauen uns Ihre Bäume in Ruhe an und beraten ehrlich. Wenn
              Pflege reicht, fällen wir nicht. Wenn gefällt werden muss, machen wir das schonend –
              auch in engen Innenhöfen, auf empfindlichem Pflaster oder mitten im Garten.
            </p>
            <p className="text-mute">
              Wir arbeiten mit Seilklettertechnik, weil sie schneller, leiser und schonender ist als
              jede Hubarbeitsbühne. Festpreise, klare Kommunikation, sauberes Aufräumen.
            </p>
          </div>
          <div
            className="aspect-[4/3] rounded-lg bg-stone bg-cover bg-center"
            role="img"
            aria-label="Klettertechnik in der Baumkrone"
            style={{ backgroundImage: "url('/images/seilklettern.jpg')" }}
          />
        </div>
      </Section>
      <ProjectsTeaser />
      <ReviewsSection />
      <FAQSection />
      <ServiceAreaSection />
      <Section background="bone" id="kontakt" ariaLabel="Kontakt">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="mb-3 text-3xl font-semibold sm:text-4xl">Kostenlose Erstberatung</h2>
            <p className="mb-6 text-mute">
              Schreiben Sie uns oder rufen Sie an. Wir melden uns innerhalb von 24 Stunden – meist
              schneller.
            </p>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="font-semibold">Telefon</dt>
                <dd>
                  <a href={company.phone.href} className="text-bark hover:underline">
                    {company.phone.display}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-semibold">E-Mail</dt>
                <dd>
                  <a href={company.email.href} className="text-bark hover:underline">
                    {company.email.primary}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-semibold">Einsatzgebiet</dt>
                <dd>{company.serviceArea}</dd>
              </div>
            </dl>
          </div>
          <ContactForm />
        </div>
      </Section>
      <Section background="paper">
        <CTABanner />
      </Section>
    </>
  );
}
